import React, { useState } from 'react';
import { Box, Paper, Typography, Link, Stack, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-hot-toast';
import { useRegister } from '../../hooks/auth/useRegister';
import { countries } from '../../constants/countres';
import FormInput from '../inputs/FormInput';
import Btn from '../global/Btn';
import { ArrowForward } from '@mui/icons-material';

const FormRegister = ({ onSuccess }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const registerHook = useRegister();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].phone);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const NewRegisterSchema = Yup.object().shape({
        name: Yup.string().required(t('validation.required')),
        email: Yup.string().required(t('validation.required')).email(t('validation.emailInvalid')),
        password: Yup.string().required(t('validation.required')).min(8, t('validation.passwordTooShort')),
        confirmPassword: Yup.string().required(t('validation.required')).oneOf([Yup.ref('password')], t('validation.passwordsNotMatch')),
    });

    const { formState: { errors } } = useForm({
        resolver: yupResolver(NewRegisterSchema),
    });

    const SubmitForm = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const fullPhone = `+${selectedCountryCode}${phoneNumber}`;

        registerHook.initiate(
            {
                name,
                email,
                phone: fullPhone,
                password,
                password_confirmation: confirmPassword,
                role: 'general_admin'
            },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_sent_success'));
                    if (onSuccess) {
                        onSuccess({
                            email,
                            phoneNumber,
                            selectedCountryCode,
                            name,
                            channel: data?.data?.channel,
                            identifier: data?.data?.identifier,
                            purpose: data?.data?.purpose,
                            expires_at: data?.data?.expires_at,
                            cooldown_seconds: data?.data?.cooldown_seconds
                        });
                    } else {
                        navigate('/login');
                    }
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.send_failed');
                    toast.error(message);
                    const errors = error?.response?.data?.errors;
                    if (errors) {
                        Object.values(errors).flat().forEach(err => toast.error(err));
                    }
                }
            }
        );
    };

    return (
        <Paper
            elevation={4}
            sx={{
                px: { md: 4, xs: 2 },
                py: { md: 4, xs: 3 },
                borderRadius: 3,
                bgcolor: 'background.paper',
            }}
        >
            <Typography variant="h5" textAlign="center" sx={{ mb: 3, fontWeight: 700 }}>
                {t('auth.signUp')}
            </Typography>

            <Box component={'form'} onSubmit={SubmitForm}>
                <Stack spacing={2}>
                    <FormInput
                        label={t('auth.fullName')}
                        placeholder={t('auth.fullName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                    />

                    <FormInput
                        label={t('auth.email')}
                        type="email"
                        value={email}
                        placeholder="example@example.com"
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />

                    <FormInput
                        type="tel"
                        label={t('auth.phone')}
                        placeholder="555-555-555"
                        selectedCountryCode={selectedCountryCode}
                        setSelectedCountryCode={setSelectedCountryCode}
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormInput
                                label={t('auth.password')}
                                type="password"
                                placeholder="********"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormInput
                                label={t('auth.confirmPassword')}
                                type="password"
                                placeholder="********"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                        <Btn
                            type="submit"
                            width="100%"
                            widthMob="100%"
                            sx={{ mx: 'auto' }}
                            loading={registerHook.isInitiating}
                            endIcon={<ArrowForward />}
                        >
                            {t('common.next')}
                        </Btn>
                    </Box>

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="body2">
                            {t('auth.alreadyHaveAccount')}{' '}
                            <Link
                                component={RouterLink}
                                to="/login"
                                sx={{ fontWeight: 600, textDecoration: 'none' }}
                            >
                                {t('auth.signIn')}
                            </Link>
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Paper>
    );
};

export default FormRegister;
